// crewbit-admin/src/pages/Money/Overview.tsx
//
// SPEC §Overview.7.2. Top-level page for /money. Composes the admin Money
// home: PageHeader, PeriodSelector, StrandedMutationBanner (no-op stub on
// admin v1), 4-card KPI strip, ActionRequiredBanner, 2-col grid
// (OutstandingAgingCard + CashFlowSparkline), TaxPrepCard.
//
// Refresh policy is OWNED by useFinancialSummary (30s focus throttle via
// Page Visibility API, mount-fetch on activeBusinessId/period change,
// cache-invalidate on MONEY_MUTATION_EVENT). This page does NOT add any
// visibility/focus listener of its own (§10.10 — would double-fire).
//
// Cmd+K palette deferred (§AdminCmdKPalette / §2.6). Header `+` is the
// only verb-launcher in v1.

import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertCircle, Building2, RefreshCw } from 'lucide-react';
import { PageHeader } from '../../components/common/PageHeader';
import { MetricCard } from '../../components/common/MetricCard';
import { Skeleton } from '../../components/common/Skeleton';
import StrandedMutationBanner from '../../components/money/StrandedMutationBanner';
import { PeriodSelector } from '../../components/money/PeriodSelector';
import { HeaderQuickActionsButton } from '../../components/money/HeaderQuickActionsButton';
import SettingsGearLink from '../../components/money/settings/SettingsGearLink';
import {
  ActionRequiredBanner,
  type ActionRequiredItem,
} from '../../components/money/ActionRequiredBanner';
import { OutstandingAgingCard } from '../../components/money/OutstandingAgingCard';
import { CashFlowSparkline } from '../../components/money/CashFlowSparkline';
import { TaxPrepCard } from '../../components/money/TaxPrepCard';
import { MoneyBrowseAllSection } from '../../components/money/MoneyBrowseAllSection';
import { RecentActivityCard } from '../../components/money/RecentActivityCard';
import {
  useFinancialSummary,
  type FinancialSummary,
  type FinancialSummaryPeriod,
} from '../../hooks/useFinancialSummary';
import { useUserPreferences, type MoneyHeroDefault } from '../../hooks/useUserPreferences';
import { useUpdateUserMoneyPreferences } from '../../hooks/useUpdateUserMoneyPreferences';
import { MoneyHero } from '../../components/money/MoneyHero';
import { formatMoney } from '../../lib/money';
import { lookupErrorCopy } from '../../lib/errorMap';
import type { MoneyError } from '../../lib/errorMap';
import { admin, palette, radii, shadow, spacing } from '../../styles/tokens';

interface PoPDelta {
  pct: number;
  type: 'up' | 'down';
}

function computeDelta(current: number, prior: number | null): PoPDelta | null {
  if (prior === null) return null;
  if (prior === 0) return null;
  const pct = ((current - prior) / Math.abs(prior)) * 100;
  if (!Number.isFinite(pct)) return null;
  if (Math.abs(pct) < 0.5) return null;
  return { pct, type: pct >= 0 ? 'up' : 'down' };
}

function isOverdueDominant(o: FinancialSummary['outstanding']): boolean {
  if (o.total <= 0) return false;
  const overdue = o.buckets['31_60'].amount + o.buckets['61_plus'].amount;
  return overdue / o.total > 0.4;
}

export function MoneyOverviewAdmin() {
  const prefs = useUserPreferences();
  const [periodOverride, setPeriodOverride] = useState<FinancialSummaryPeriod | null>(null);
  const period: FinancialSummaryPeriod =
    periodOverride ?? prefs.data?.moneyPeriodDefault ?? '30d';
  const summary = useFinancialSummary({ period });

  // C3 P3 — configurable headline (mirrors mobile's moneyHeroDefault). Local
  // override gives instant feedback; the write persists the pref (the period
  // selector above follows the same override-then-persist pattern).
  const [heroOverride, setHeroOverride] = useState<MoneyHeroDefault | null>(null);
  const heroKind: MoneyHeroDefault = heroOverride ?? prefs.data?.moneyHeroDefault ?? 'revenue_month';
  const updatePrefs = useUpdateUserMoneyPreferences();
  const onHeroKindChange = (k: MoneyHeroDefault) => {
    setHeroOverride(k);
    void updatePrefs({ moneyHeroDefault: k });
  };

  useEffect(() => {
    document.title = 'CrewBit · Money';
  }, []);

  // Branch 1 — RLS / no-business gate. Outer <RequiresActiveBusinessGate>
  // already short-circuits at the route boundary; this is a defensive
  // fallback for transitions.
  if (summary.requiresBusinessSelection) {
    return <PickBusinessGate />;
  }

  // Branch 2 — initial load. Stale-while-revalidate kicks in once data exists.
  if (summary.isLoading && !summary.data) {
    return (
      <div style={pageStyle}>
        <PageHeader
          title="Money"
          description="Last 30 days · vs prior 30 days"
          primaryAction={<HeaderQuickActionsButton />}
          secondaryActions={<SettingsGearLink />}
        />
        <PeriodSelector value={period} onChange={setPeriodOverride} />
        <MoneyOverviewSkeleton />
      </div>
    );
  }

  // Branch 3 — first-load error (data still null).
  if (summary.error && !summary.data) {
    return (
      <div style={pageStyle}>
        <PageHeader
          title="Money"
          description="Last 30 days · vs prior 30 days"
          primaryAction={<HeaderQuickActionsButton />}
          secondaryActions={<SettingsGearLink />}
        />
        <PeriodSelector value={period} onChange={setPeriodOverride} />
        <MoneyOverviewError error={summary.error} onRetry={summary.refetch} />
      </div>
    );
  }

  // Branch 4 — happy path. `summary.data` is non-null here.
  const data = summary.data;
  if (!data) return null;

  return (
    <HappyPath
      data={data}
      period={period}
      onPeriodChange={setPeriodOverride}
      heroKind={heroKind}
      onHeroKindChange={onHeroKindChange}
    />
  );
}

interface HappyPathProps {
  data: FinancialSummary;
  period: FinancialSummaryPeriod;
  onPeriodChange: (p: FinancialSummaryPeriod) => void;
  heroKind: MoneyHeroDefault;
  onHeroKindChange: (k: MoneyHeroDefault) => void;
}

function HappyPath({ data, period, onPeriodChange, heroKind, onHeroKindChange }: HappyPathProps) {
  const navigate = useNavigate();
  const moneyInDelta = useMemo(
    () => computeDelta(data.money_in, data.prior_money_in),
    [data.money_in, data.prior_money_in],
  );
  const netDelta = useMemo(
    () => computeDelta(data.net, data.prior_net),
    [data.net, data.prior_net],
  );
  const overdueDominant = useMemo(() => isOverdueDominant(data.outstanding), [data.outstanding]);

  const description = `${data.period.label} · vs prior period`;

  const outstandingSub =
    data.outstanding.invoice_count > 0
      ? `${data.outstanding.invoice_count} invoice${data.outstanding.invoice_count === 1 ? '' : 's'} · oldest ${data.outstanding.oldest_days}d`
      : undefined;

  const taxOwingDueSoon =
    data.tax_summary.days_until_due !== null && data.tax_summary.days_until_due <= 30;
  const taxOwingSub =
    data.tax_summary.days_until_due !== null
      ? `due in ${data.tax_summary.days_until_due}d`
      : undefined;

  const actionItems: ActionRequiredItem[] =
    data.disputes_open.count > 0
      ? [
          {
            kind: 'disputes_open',
            count: data.disputes_open.count,
            amount: data.disputes_open.amount,
          },
        ]
      : [];

  return (
    <div style={pageStyle}>
      <PageHeader
        title="Money"
        description={description}
        primaryAction={<HeaderQuickActionsButton />}
        secondaryActions={<SettingsGearLink />}
      />

      <div style={{ marginBottom: spacing.lg }}>
        <PeriodSelector value={period} onChange={onPeriodChange} />
      </div>

      <StrandedMutationBanner />

      {/* C3 P3 — configurable headline above the strip (D-A2: strip stays as the truths). */}
      <MoneyHero kind={heroKind} data={data} onKindChange={onHeroKindChange} />

      {/* KPI strip — 4 cards */}
      <div
        style={{
          display: 'flex',
          gap: spacing.md,
          marginBottom: spacing.lg,
          flexWrap: 'wrap',
        }}
      >
        <MetricCard
          label="Money in"
          value={formatMoney(data.money_in)}
          delta={moneyInDelta ? `${Math.abs(moneyInDelta.pct).toFixed(1)}%` : undefined}
          deltaType={moneyInDelta?.type}
          onClick={() => navigate('/money/revenue')}
        />
        <MetricCard
          label="Outstanding"
          value={formatMoney(data.outstanding.total)}
          valueColor={overdueDominant ? palette.amber : undefined}
          sub={outstandingSub}
          onClick={() => navigate('/money/outstanding')}
        />
        <MetricCard
          label="Net"
          value={formatMoney(data.net)}
          valueColor={data.net < 0 ? palette.red : undefined}
          delta={netDelta ? `${Math.abs(netDelta.pct).toFixed(1)}%` : undefined}
          deltaType={netDelta?.type}
          onClick={() => navigate('/money/revenue')}
        />
        <MetricCard
          label="HST owing"
          value={formatMoney(data.tax_summary.net_owing)}
          valueColor={
            taxOwingDueSoon && data.tax_summary.net_owing > 0 ? palette.amber : undefined
          }
          sub={taxOwingSub}
          deltaType={taxOwingDueSoon ? 'warn' : undefined}
          onClick={() => navigate('/money/tax')}
        />
      </div>

      <ActionRequiredBanner
        items={actionItems}
        onItemTap={(item) => {
          if (item.kind === 'disputes_open') void navigate('/money/disputes');
        }}
      />

      {/* 2-col grid: OutstandingAgingCard | CashFlowSparkline (1 : 1.4 ratio) */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1.4fr)',
          gap: spacing.md,
          marginBottom: spacing.lg,
        }}
      >
        <OutstandingAgingCard
          buckets={data.outstanding.buckets}
          onClick={() => navigate('/money/outstanding')}
          onBucketClick={(key) => navigate(`/money/outstanding?bucket=${key}`)}
        />
        <CashFlowSparkline
          periodLabel={data.period.label}
          onClick={() => navigate('/money/revenue')}
        />
      </div>

      <MoneyBrowseAllSection />

      <RecentActivityCard items={data.recent_activity} onSeeAll={() => navigate('/money/activity')} />

      <TaxPrepCard summary={data.tax_summary} />
      {/* TaxPrepCard self-gates on days_until_due ≤ 60 && status === 'open'. */}
    </div>
  );
}

const pageStyle: React.CSSProperties = {
  padding: spacing.xl,
  background: admin.bg,
  minHeight: '100vh',
};

function MoneyOverviewSkeleton() {
  return (
    <div>
      {/* KPI strip skeleton — 4 cards */}
      <div style={{ display: 'flex', gap: spacing.md, marginBottom: spacing.lg }}>
        {[0, 1, 2, 3].map((i) => (
          <div
            key={i}
            style={{
              flex: 1,
              minWidth: 0,
              padding: '16px 18px',
              background: palette.white,
              border: `1px solid ${admin.border}`,
              borderRadius: admin.cardRadius,
            }}
          >
            <Skeleton width={80} height={10} />
            <div style={{ height: 12 }} />
            <Skeleton width={120} height={26} />
            <div style={{ height: 8 }} />
            <Skeleton width={60} height={10} />
          </div>
        ))}
      </div>
      {/* 2-col grid skeleton */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1.4fr)',
          gap: spacing.md,
        }}
      >
        <Skeleton height={240} radius={admin.cardRadius} />
        <Skeleton height={240} radius={admin.cardRadius} />
      </div>
    </div>
  );
}

interface MoneyOverviewErrorProps {
  error: MoneyError;
  onRetry: () => Promise<void>;
}

function MoneyOverviewError({ error, onRetry }: MoneyOverviewErrorProps) {
  const copy = lookupErrorCopy('financial_summary', error.code);
  return (
    <div
      style={{
        background: palette.amberWash,
        border: `1px solid ${palette.amber}`,
        borderRadius: admin.cardRadius,
        padding: spacing.lg,
        display: 'flex',
        alignItems: 'center',
        gap: spacing.md,
        boxShadow: shadow.card,
      }}
    >
      <AlertCircle size={20} color={palette.amber} strokeWidth={2} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: palette.amber }}>
          We couldn't load your Money home.
        </div>
        <div style={{ fontSize: 13, color: palette.amber, marginTop: 4 }}>
          {copy}
        </div>
      </div>
      <button
        type="button"
        onClick={() => void onRetry()}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          padding: '8px 14px',
          borderRadius: radii.sm,
          border: 'none',
          background: palette.amber,
          color: palette.white,
          fontSize: 13,
          fontWeight: 600,
          fontFamily: 'inherit',
          cursor: 'pointer',
        }}
      >
        <RefreshCw size={13} />
        Retry
      </button>
    </div>
  );
}

function PickBusinessGate() {
  return (
    <div style={pageStyle}>
      <div
        style={{
          background: palette.white,
          border: `1px solid ${admin.border}`,
          borderRadius: admin.cardRadius,
          padding: spacing.xxl,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: spacing.md,
          textAlign: 'center',
          boxShadow: shadow.card,
        }}
      >
        <div
          style={{
            width: 48,
            height: 48,
            borderRadius: radii.md,
            background: palette.accentWash,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Building2 size={22} color={palette.accent} strokeWidth={2} />
        </div>
        <div style={{ fontSize: 17, fontWeight: 600, color: admin.header }}>
          Pick a business
        </div>
        <div style={{ fontSize: 13.5, color: admin.caption, maxWidth: 360 }}>
          Switch to an active business from the sidebar header to see your Money home.
        </div>
      </div>
    </div>
  );
}
